import React, { useEffect, useState } from "react";

import { Grid, Tooltip, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziGrid from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiCopy, FiEdit } from "react-icons/fi";
import { MdOutlineCancel } from "react-icons/md";
import { Prompt } from "react-router-dom";

import { Button } from "../../../../../../components/Form";
import { Color } from "../../../../../../components/Form/Button";
import { CountrySuggest } from "Components/Form/CountrySuggest";
import TextField from "../../../../../../components/Form/Input";
import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import SkeletonComponent from "../../../../../../components/Generic/Skeleton";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { APIConfig } from "../../../../../../models/api.config";
import { APIConfigEmail } from "../../../../../../models/api.config.email";
import { TokenAction } from "../../../../../../models/token.action";
import ConfigEmailService from "../../../../../../services/config.email.service";
import ConfigService from "../../../../../../services/config.service";
import TokenActionService from "../../../../../../services/token.action.service";
import { TokenType } from "../../../../../../util/enums";
import { ModalToInviteSistrs } from "./ModalToInviteSistrs";
import useStyles from "./styles";

type Props = { onChange?: (hasUnsavedChanges?: boolean) => void };

const APIAllied = (props: Props) => {
  const { onChange } = props;

  const { t: translate } = useTranslation();

  const { currentUser, hasUnsavedChanges, setHasUnsavedChanges } = useAppState();

  const classes = useStyles();

  const [APIConfig, setAPIConfig] = useState<APIConfig>();

  const [configEmails, setConfigEmails] = useState<APIConfigEmail[]>([]);
  const [configEmailSelected, setConfigEmailSelected] = useState<APIConfigEmail>();
  const [indexConfigEmailSelected, setIndexConfigEmailSelected] = useState<number>();

  const [configEmailsDeleted, setConfigEmailsDeleted] = useState<APIConfigEmail[]>([]);
  const [total, setTotal] = useState<number>(0);

  const [openInvite, setOpenInvite] = useState<boolean>(false);

  const [tokenAction, setTokenAction] = useState<TokenAction>({});

  const { isFetching: isLoadingToken } = useQuery(
    ["tokenAction.get.all"],
    () =>
      TokenActionService.getAll({
        type: TokenType.QUOTATION_COMPANY,
        targetCompanyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "country" }]),
      }),
    {
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (
          response &&
          response.data &&
          Array.isArray(response.data) &&
          response?.data?.length > 0
        ) {
          setTokenAction(response?.data[0]);
        }
      },
    }
  );

  const { mutateAsync: createConfig } = useMutation(({ APIConfig }: { APIConfig: APIConfig }) =>
    ConfigService.create(APIConfig)
  );

  useQuery(
    ["config.get.all"],
    () =>
      ConfigService.getAll({
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (
          response &&
          response.data &&
          Array.isArray(response.data) &&
          response?.data?.length > 0
        ) {
          setAPIConfig(response?.data[0]);
        }
      },
    }
  );

  const { isFetching: loading, refetch: refetchConfigEmail } = useQuery(
    ["config.email.list"],
    () =>
      ConfigEmailService.getAll({
        include: JSON.stringify([
          {
            association: "apiConfig",
            required: true,
            where: { companyId: currentUser?.companyId },
          },
        ]),
      }),
    {
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => setConfigEmails(response.data),
    }
  );

  const { refetch: refetchCountList } = useQuery(
    ["config.email.count"],
    () =>
      ConfigEmailService.count({
        include: JSON.stringify([
          {
            association: "apiConfig",
            required: true,
            where: { companyId: currentUser?.companyId },
          },
        ]),
      }),
    {
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => setTotal(Number(response?.data) || 0),
    }
  );

  const { mutateAsync: createTokenAction, isLoading: isCreatingToken } = useMutation(
    ({ tokenAction }: { tokenAction: TokenAction }) => TokenActionService.create(tokenAction),
    { onError: () => {} }
  );

  const { mutateAsync: updateTokenAction, isLoading: isUpdatingToken } = useMutation(
    ({ id, tokenAction }: { id: number; tokenAction: TokenAction }) =>
      TokenActionService.update(id, tokenAction),
    { onError: () => {} }
  );

  const { mutateAsync: createListConfigEmail, isLoading: isCreatingListConfigEmail } = useMutation(
    ({ configEmails }: { configEmails: APIConfigEmail[] }) =>
      ConfigEmailService.createList(configEmails),
    { onError: () => {} }
  );

  const { mutateAsync: updateListConfigEmail, isLoading: isUpdatingListConfigEmail } = useMutation(
    ({ configEmails }: { configEmails: APIConfigEmail[] }) =>
      ConfigEmailService.updateList(configEmails),
    { onError: () => {} }
  );

  const { mutateAsync: deleteListConfigEmail, isLoading: isDeletingListConfigEmail } = useMutation(
    ({ configEmails }: { configEmails: APIConfigEmail[] }) =>
      ConfigEmailService.deleteList(configEmails),
    { onError: () => {} }
  );

  useEffect(() => {
    if (configEmailSelected) {
      setOpenInvite(true);
    } else {
      setOpenInvite(false);
    }
  }, [configEmailSelected]);

  useEffect(() => {
    if (onChange) onChange(hasUnsavedChanges);
  }, [hasUnsavedChanges]);

  const onSubmitTokenAction = async () => {
    if (tokenAction) {
      if (tokenAction?.id) {
        await updateTokenAction({ id: tokenAction?.id, tokenAction });
      } else {
        await createTokenAction({
          tokenAction: {
            ...tokenAction,
            type: TokenType.QUOTATION_COMPANY,
            targetCompanyId: currentUser?.companyId,
          },
        });
      }
    }
  };

  const onSubmitSISTRS = async () => {
    const configEmailsUpdated: APIConfigEmail[] = [];
    const configEmailsCreated: APIConfigEmail[] = [];

    let APIConfigFound: APIConfig | null = null;

    if (APIConfig && APIConfig?.id) {
      APIConfigFound = APIConfig;
    } else {
      const response = await createConfig({ APIConfig: { companyId: currentUser?.companyId } });

      if (response && response?.data) APIConfigFound = response?.data;
    }

    if (configEmails && configEmails?.length > 0) {
      configEmails.forEach((configEmail) => {
        if (configEmail?.id) {
          configEmailsUpdated.push(configEmail);
        } else if (APIConfig && APIConfig?.id) {
          configEmailsCreated.push({ ...configEmail, apiConfigId: APIConfigFound?.id });
        }
      });
    }

    if (configEmailsUpdated && configEmailsUpdated?.length > 0)
      await updateListConfigEmail({ configEmails: configEmailsUpdated });
    if (configEmailsCreated && configEmailsCreated?.length > 0)
      await createListConfigEmail({ configEmails: configEmailsCreated });
    if (configEmailsDeleted && configEmailsDeleted?.length > 0)
      await deleteListConfigEmail({ configEmails: configEmailsDeleted });
  };

  const onSave = async () => {
    try {
      await onSubmitTokenAction();
      await onSubmitSISTRS();
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.notification.success.api.allied",
      });
      refetchConfigEmail();
      refetchCountList();
      setHasUnsavedChanges(false);
    } catch (error) {
      NotificationToast.error(
        translate("anErrorOccurredTryAgain", {
          id: "request.notification.error.api.allied",
        })
      );
    }
  };

  const emailAlreadyExists = () => {
    NotificationToast.warning(
      translate("emailAlreadyExists", {
        id: "request.notification.warning.email",
      })
    );

    return false;
  };

  const hasChangedConfigEmail = (configEmail?: APIConfigEmail) => {
    if (configEmail) {
      const configEmailsUpdated: APIConfigEmail[] = [...(configEmails || [])];

      const configEmailFoundIndex = configEmailsUpdated.findIndex(
        (configEmailUpdated) => configEmailUpdated?.email?.trim() === configEmail?.email?.trim()
      );

      if (indexConfigEmailSelected !== undefined) {
        if (configEmailFoundIndex !== -1 && configEmailFoundIndex !== indexConfigEmailSelected)
          return emailAlreadyExists();

        configEmailsUpdated[indexConfigEmailSelected] = {
          ...configEmailsUpdated[indexConfigEmailSelected],
          ...configEmail,
        };
      } else {
        if (configEmailFoundIndex !== -1) return emailAlreadyExists();

        configEmailsUpdated.push(configEmail);
      }
      setConfigEmails(configEmailsUpdated);
      setHasUnsavedChanges(true);
    }
    setConfigEmailSelected(undefined);
    setIndexConfigEmailSelected(undefined);

    return true;
  };

  const removeEmailFromList = (configEmail: APIConfigEmail) => {
    const configEmailsUpdated: APIConfigEmail[] = [...(configEmails || [])];

    setConfigEmails(
      configEmailsUpdated.filter(
        (configEmailUpdated) => configEmailUpdated?.email !== configEmail?.email
      )
    );

    if (configEmail && configEmail?.id) {
      const configEmailsDeletedUpdated: APIConfigEmail[] = [...(configEmailsDeleted || [])];

      configEmailsDeletedUpdated.push(configEmail);
      setConfigEmailsDeleted(configEmailsDeletedUpdated);
    }

    setHasUnsavedChanges(true);
  };

  const renderEditButton = (index: number, configEmail: APIConfigEmail) => {
    return (
      <Tooltip data-cy={`action-edit-${index}`} title={translate("edit")} placement="top">
        <div>
          <FiEdit
            color="#475467"
            onClick={() => {
              setConfigEmailSelected(configEmail);
              setIndexConfigEmailSelected(Number(index));
            }}
            className={classes.button}
            size={18}
          />
        </div>
      </Tooltip>
    );
  };

  const renderRemoveButton = (index: number, configEmail: APIConfigEmail) => {
    return (
      <Tooltip data-cy={`action-remove-${index}`} title={translate("remove")} placement="top">
        <div>
          <MdOutlineCancel
            size={20}
            className={classes.button}
            data-cy="removeButton"
            color="#F97066"
            onClick={() => removeEmailFromList(configEmail)}
          />
        </div>
      </Tooltip>
    );
  };

  return (
    <Grid
      direction="column"
      margin="24px"
      borderRadius="12px"
      border="1px solid #E4E7EC"
      padding="24px"
      spacing={2}
    >
      <Prompt when={hasUnsavedChanges} message="" />
      <Typography
        marginY="24px"
        fontFamily="Manrope"
        fontWeight={700}
        fontSize="16px"
        color="#465869"
      >
        {translate("amos")}
      </Typography>
      <Grid item container direction="row" marginBottom="24px" spacing={2}>
        <Grid item container direction="row" alignItems="flex-end" spacing={1} xs={6}>
          <Grid item xs={9}>
            <SkeletonComponent isLoading={isLoadingToken}>
              <TextField
                name="token"
                data-cy="token"
                placeholder={translate("token")}
                label={translate("amosToken")}
                onChange={(event) => {
                  setHasUnsavedChanges(true);
                  setTokenAction({ ...tokenAction, token: event.target.value });
                }}
                value={tokenAction?.token}
                disabled={isCreatingToken || isUpdatingToken}
              />
            </SkeletonComponent>
          </Grid>
          <Grid item xs={3}>
            <SkeletonComponent isLoading={isLoadingToken}>
              <Button
                startIcon={<FiCopy color="#FFFFFF" />}
                onClick={() => {
                  if (tokenAction && tokenAction?.token)
                    navigator.clipboard.writeText(tokenAction?.token);
                  NotificationToast.success(translate("copiedSuccessfully"));
                }}
                customColor={Color.SECONDARY}
              >
                {translate("copyKey")}
              </Button>
            </SkeletonComponent>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <SkeletonComponent isLoading={isLoadingToken}>
            <CountrySuggest
              label={translate("country")}
              placeholder={translate("australia")}
              country={tokenAction?.country}
              onChange={(country) => {
                setHasUnsavedChanges(true);
                setTokenAction({ ...tokenAction, country, countryId: country?.id });
              }}
              disabled={isCreatingToken || isUpdatingToken}
            />
          </SkeletonComponent>
        </Grid>
      </Grid>
      <ShipeeziGrid
        titlePage={
          <Typography
            marginY="24px"
            fontFamily="Manrope"
            fontWeight={700}
            fontSize="18px"
            color="#465869"
          >
            {translate("sistrs")}
          </Typography>
        }
        columns={[{ name: "email", title: translate("email"), hideSort: true }]}
        data={configEmails}
        keyExtractor={(configEmail: APIConfigEmail, index) =>
          index?.toString() || configEmail?.id?.toString() || configEmail?.email || ""
        }
        generalActions={{
          orderBy: {
            right: ["handleNewRecord"],
          },
          handleNewRecord: {
            text: translate("add"),
            tooltip: translate("add"),
            action: () => setOpenInvite(true),
          },
        }}
        rowActions={{
          orderBy: ["edit", "remove"],
        }}
        rowActionsExtra={[
          {
            title: translate("edit"),
            key: "edit",
            buttonAndAction: renderEditButton,
          },
          {
            title: translate("remove"),
            key: "remove",
            buttonAndAction: renderRemoveButton,
          },
        ]}
        totalItems={total}
        loadingData={loading}
        emptyData={translate("noInformationToBeShown")}
        positionActionsColumn="right"
      />
      <Grid marginTop="24px" container xs={12} alignItems="center" justifyContent="flex-end">
        <ActionButtons
          onSave={onSave}
          loading={
            isCreatingToken ||
            isUpdatingToken ||
            isUpdatingListConfigEmail ||
            isCreatingListConfigEmail ||
            isDeletingListConfigEmail
          }
        />
      </Grid>
      {openInvite && (
        <ModalToInviteSistrs
          isOpen={openInvite}
          onClose={(configEmail) => {
            if (hasChangedConfigEmail(configEmail)) setOpenInvite(false);
          }}
          configEmail={configEmailSelected}
        />
      )}
    </Grid>
  );
};

export default APIAllied;
